'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import type { FormConfig, FormField } from '@/lib/capturas/types'

interface Props {
  slug: string
  config: FormConfig
  userEmail?: string | null
  userName?: string | null
  userPhone?: string | null
}

function formatPhoneBr(value: string): string {
  const d = value.replace(/\D/g, '')
  if (d.length <= 2) return d
  if (d.length <= 7) return '(' + d.slice(0, 2) + ') ' + d.slice(2)
  if (d.length <= 11) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7)
  return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7, 11)
}

export function CaptureForm({ slug, config, userEmail, userName, userPhone }: Props) {
  const router = useRouter()
  const fields: FormField[] = config.fields || []

  const initial: Record<string, string> = {}
  for (const f of fields) {
    if (f.key === 'email' && userEmail) initial[f.key] = userEmail
    else if (f.key === 'name' && userName) initial[f.key] = userName
    else if ((f.key === 'phone' || f.type === 'tel-br') && userPhone) {
      initial[f.key] = formatPhoneBr(userPhone.replace(/^\+55/, ''))
    }
    else initial[f.key] = ''
  }

  const [values, setValues] = useState<Record<string, string>>(initial)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function update(key: string, v: string, type: string) {
    if (type === 'tel-br') v = formatPhoneBr(v)
    setValues({ ...values, [key]: v })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    for (const f of fields) {
      if (f.required && !values[f.key]?.trim()) {
        setError(`Campo "${f.label}" e obrigatorio`)
        return
      }
    }
    setError('')
    setLoading(true)

    // Normalizar telefone BR para E.164
    const payload: Record<string, string> = {}
    for (const f of fields) {
      const v = values[f.key]?.trim()
      if (!v) continue
      if (f.type === 'tel-br') {
        const digits = v.replace(/\D/g, '')
        payload[f.key] = digits ? '+55' + digits : ''
      } else {
        payload[f.key] = v
      }
    }

    // UTMs
    try {
      const params = new URLSearchParams(window.location.search)
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
        const val = params.get(key)
        if (val) payload[key] = val
      }
    } catch {/* ignore */}

    try {
      const res = await fetch(`/api/capture/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Erro ao enviar')
      }
      if (config.success_message) {
        setError('')
        setLoading(false)
        alert(config.success_message)
        return
      }
      if (data.redirect_url) {
        router.push(data.redirect_url)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Algo deu errado. Tente novamente.')
      setLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-xl shadow-stone-200/50 p-6 sm:p-8">
      <div className="text-center mb-6">
        {config.badge && (
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-bold mb-3">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            {config.badge}
          </div>
        )}
        {config.title && <h3 className="text-xl font-black text-stone-900 mb-1">{config.title}</h3>}
        {config.subtitle && <p className="text-sm text-stone-500">{config.subtitle}</p>}
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {fields.map((f) => (
          <div key={f.key}>
            <label htmlFor={f.key} className="block text-sm font-semibold text-stone-700 mb-1.5">
              {f.label}
              {!f.required && <span className="text-stone-400 font-normal"> (opcional)</span>}
            </label>
            {f.type === 'tel-br' ? (
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 px-3 py-3 bg-stone-100 border border-stone-200 rounded-xl text-sm text-stone-600 shrink-0">
                  <span>+55</span>
                </div>
                <input
                  id={f.key}
                  type="tel"
                  value={values[f.key] || ''}
                  onChange={(e) => update(f.key, e.target.value, f.type)}
                  placeholder={f.placeholder || '(11) 99999-9999'}
                  required={f.required}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/30 focus:border-[#006b5f] transition-all"
                />
              </div>
            ) : f.type === 'textarea' ? (
              <textarea
                id={f.key}
                value={values[f.key] || ''}
                onChange={(e) => update(f.key, e.target.value, f.type)}
                placeholder={f.placeholder}
                required={f.required}
                rows={4}
                className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/30 focus:border-[#006b5f] transition-all"
              />
            ) : (
              <input
                id={f.key}
                type={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text'}
                value={values[f.key] || ''}
                onChange={(e) => update(f.key, e.target.value, f.type)}
                placeholder={f.placeholder}
                required={f.required}
                className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/30 focus:border-[#006b5f] transition-all"
              />
            )}
          </div>
        ))}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 bg-[#006b5f] hover:bg-[#005a50] disabled:bg-stone-300 text-white font-bold rounded-xl transition-all text-base shadow-lg shadow-[#006b5f]/20 cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Enviando...
            </>
          ) : (
            <>
              {config.submit_label || 'Enviar'}
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>
          )}
        </button>

        {config.privacy_note && (
          <p className="text-xs text-stone-400 text-center">{config.privacy_note}</p>
        )}
      </form>
    </div>
  )
}
